import {
  ADMIN_METRIC_ACCENT_TEXT,
  ADMIN_PALETTE,
} from '@/config/adminChartTheme';
import React, { useEffect, useState } from 'react';
import ChartCard from '../ChartCard';
import { fetchBudgetOverview } from '../../../services/campaignAnalyticsService';
import millify from 'millify';

interface BudgetOverview {
  totalDeposited: number;
  totalDeployed: number;
  totalRemaining: number;
  activeBudget: number;
  activationRate: number;
}

interface BudgetOverviewCardProps {
  refreshToken?: number;
}

const BudgetOverviewCard: React.FC<BudgetOverviewCardProps> = ({
  refreshToken,
}) => {
  const [data, setData] = useState<BudgetOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBudgetOverview()
      .then(setData)
      .finally(() => setLoading(false));
  }, [refreshToken]);

  const rows = data
    ? [
        {
          label: 'Total Deposited',
          value: data.totalDeposited,
          color: ADMIN_PALETTE.primary,
        },
        {
          label: 'Total Deployed',
          value: data.totalDeployed,
          color: ADMIN_PALETTE.accent,
        },
        {
          label: 'Remaining',
          value: data.totalRemaining,
          color: ADMIN_PALETTE.muted,
        },
        {
          label: 'Active Budget',
          value: data.activeBudget,
          color: ADMIN_PALETTE.warm,
        },
      ]
    : [];

  return (
    <ChartCard
      title="Budget Overview"
      subtitle="Platform-wide budget allocation"
      loading={loading}
      actions={
        data ? (
          <div className="text-right">
            <p className="text-xs text-[#9B919D]">Activation Rate</p>
            <p className={`text-lg font-bold ${ADMIN_METRIC_ACCENT_TEXT}`}>
              {data.activationRate}%
            </p>
          </div>
        ) : undefined
      }
    >
      {data && (
        <>
          <div className="relative h-3 bg-[#F5F5F5] rounded-full overflow-hidden mb-6">
            <div
              className="absolute left-0 top-0 h-full rounded-full transition-all duration-700"
              style={{
                width: `${data.activationRate}%`,
                background: `linear-gradient(90deg, ${ADMIN_PALETTE.primary}, ${ADMIN_PALETTE.mid})`,
              }}
            />
          </div>
          <div className="space-y-3">
            {rows.map(({ label, value, color }) => (
              <div key={label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-xs text-[#6B778C]">{label}</span>
                </div>
                <span className="text-sm font-semibold text-[#172B4D]">
                  €{millify(value)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </ChartCard>
  );
};

export default BudgetOverviewCard;
